import { useEffect, useMemo, useRef, useState } from "react";
import { currentDay, currentYear } from "../../shared";
import { buildChartUrl, fetchBirthChart, fetchDayChart, fetchDivisionalChart, fetchMonthChart, fetchTransitChart, fetchUserKarkas, fetchYearChart } from "./chartService";
import { ChartSlider, ChartWithLoader } from "../Chart";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DivisionalChartType, MonthEnum, PadithiEnum, PredictionType } from "../../utils/enums";
import { KarkaDetail } from "../karka/Karka";
import moment from "moment";
import { getCurrentDayInTimezone, getCurrentMonthInTimezone, getMonthAccordingToBirthdayDay, getPredictionYearByMonth, getTargetYear, getYearAccordingToBirthday } from "../../utils/dateFromat";
import { monthlyChartInterval } from "./chart.util";

export const UserCharts = ({ userId, user, padithi, selectedYear, selectedMonth, selectedDay, selectedTimezone, predictionType }) => {

	const [dayChart, setDayChart] = useState(null);
	const [birthChart, setBirthChart] = useState(null);

	const [transitDate, setTransitDate] = useState(null);
	const [transitDay, setTransitDay] = useState(moment().date());
	const [transitMonth, setTransitMonth] = useState(moment().month() + 1);
	const [transitYear, setTransitYear] = useState(moment().year());
	const [daysInTransitMonth, setDaysInTransitMonth] = useState([]);

	const birthDate = user?.birthDetails?.date
	const birthTZ = user?.birthDetails?.timezone

	const timezone =
		selectedTimezone ??
		user?.userCurrentTimezone ??
		Intl.DateTimeFormat().resolvedOptions().timeZone;

	// compute the default year/month/day (memoized)
	const year = useMemo(
		() => selectedYear || getYearAccordingToBirthday(birthDate, timezone, birthTZ) || currentYear,
		[selectedYear, birthDate, timezone, birthTZ]
	);

	const month = useMemo(() => {
		if (Number(selectedMonth)) {
			return Number(selectedMonth);
		}
		return getCurrentMonthInTimezone(timezone);

	}, [selectedMonth, timezone]);


	const day = useMemo(
		() => selectedDay || getCurrentDayInTimezone(timezone) || currentDay,
		[selectedDay, timezone]
	);

	const getPredictionYear = selectedYear || getTargetYear({ birthDate, birthTZ, currentTZ: timezone, targetMonth: month, targetDay: day })

	const [chartUrls, setChartUrls] = useState({
		birthChartUrl: '',
		monthChartUrl: '',
		yearChartUrl: '',
		dayChartUrl: '',
		transitChartUrl: '',
		chaturthamsaChartUrl: '',
		saptamsaChartUrl: '',
		navamsaChartUrl: '',
		dasamsaChartUrl: '',
		dwadamsaChartUrl: ''
	});

	const [karkas, setKarkas] = useState([])

	const [chartLoading, setChartLoading] = useState({
		birth: false,
		year: false,
		month: false,
		day: false,
		transit: false,
		chaturthamsa: false,
		saptamsa: false,
		navamsa: false,
		dasamsa: false,
		dwadamsa: false,
		karkas: false
	});

	const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

	const minWidth = 200;
	const maxWidth = 225;
	const minHeight = 170;

	const previousUserRef = useRef(null);
	const isFetchingInitialCharts = useRef(false);
	let fetchInitialCharts = useRef(false);

	// Format date for interval display without timezone conversion
	const formatIntervalDate = (isoString) => {
		if (!isoString) return "";

		try {
			// Extract YYYY-MM-DD directly from the ISO string
			// const [ month, day] = isoString.split("T")[0].split("-");
			// const date = convertDateToUserTimezoneWithTime(isoString, timezone)

			const date = moment(isoString).tz(timezone)
			const day = date.date();
			const month = date.month()

			// Convert month to name
			const monthName = monthNames[Number(month)];

			return `${Number(day)} ${monthName}`;
		} catch (error) {
			console.error("Error formatting interval date:", error);
			return "";
		}
	};

	const formatMonthlyIntervalDate = (date) => {
		try {
			const month = date.month()
			const day = date.date()

			const monthName = monthNames[Number(month)];
			return `${Number(day)} ${monthName}`;
		} catch (error) {
			return ''
		}
	}
	// Format interval for display: "1 Dec - 31 Dec"
	const formatInterval = () => {

		let formattedStart = ''
		let formattedEnd = ''

		if (predictionType === PredictionType.MONTH) {
			if (!year || !month) return ''
			const chartMonth = selectedMonth === MonthEnum.CURRENT_MONTH ? getMonthAccordingToBirthdayDay(birthDate, timezone, birthTZ) : month
			const date = monthlyChartInterval(year, chartMonth, birthDate, birthTZ, timezone)

			formattedStart = formatMonthlyIntervalDate(date?.start)
			formattedEnd = formatMonthlyIntervalDate(date?.end)

		} else {
			const startDate = dayChart?.interval?.startDate;
			const endDate = dayChart?.interval?.endDate;

			if (!startDate || !endDate) return "";

			formattedStart = formatIntervalDate(startDate);
			formattedEnd = formatIntervalDate(endDate);
		}
		if (formattedStart && formattedEnd) {
			return `${formattedStart} - ${formattedEnd}`;
		}
		return "";
	};



	const intervalDisplay = formatInterval();

	const resetChartLoading = () => setChartLoading({
		birth: false,
		year: false,
		month: false,
		day: false,
		transit: false,
		chaturthamsa: false,
		saptamsa: false,
		navamsa: false,
		dasamsa: false,
		dwadamsa: false,
	})

	const resetChartUrl = () =>
		setChartUrls({
			birthChartUrl: '',
			monthChartUrl: '',
			yearChartUrl: '',
			dayChartUrl: '',
			transitChartUrl: '',
			chaturthamsaChartUrl: '',
			saptamsaChartUrl: '',
			navamsaChartUrl: '',
			dasamsaChartUrl: '',
			dwadamsaChartUrl: ''
		})

	const resetChartData = () => setDayChart(null)

	const resetData = () => {
		resetChartUrl()
		resetChartData()
		setKarkas([])
	}

	const createBirthChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, birth: true }));

			const response = await fetchBirthChart({ userId });

			const { birthChart } = response.data;
			if (birthChart) {
				setBirthChart(birthChart)

				const heading = birthChart[0]?.signNumber ?? 1;
				const url = buildChartUrl(birthChart, heading);

				setChartUrls(prev => ({
					...prev,
					birthChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createBirthChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, birth: false }));
		}
	};

	const createYearChart = async () => {
		try {
			setChartLoading(prev => ({ ...prev, year: true }));
			const chartYear = selectedYear ? Number(year) : null
			const response = await fetchYearChart({ userId, timezone, year: chartYear });

			const { yearChart } = response.data;
			if (yearChart) {
				const heading = yearChart[0]?.signNumber ?? 1;
				const url = buildChartUrl(yearChart, heading);

				setChartUrls(prev => ({
					...prev,
					yearChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createYearChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, year: false }));
		}
	}

	const createMonthChart = async () => {
		try {
			if (predictionType !== PredictionType.MONTH) return;

			setChartLoading(prev => ({ ...prev, month: true }));
			const chartYear = selectedYear ? Number(year) : null
			const chartMonth = selectedMonth === MonthEnum.CURRENT_MONTH ? getMonthAccordingToBirthdayDay(birthDate, timezone, birthTZ) : month

			const response = await fetchMonthChart({ userId, year: chartYear, month: chartMonth, timezone });

			const { monthChart } = response.data;
			if (monthChart) {
				const heading = monthChart[0]?.signNumber ?? 1;
				const url = buildChartUrl(monthChart, heading);

				setChartUrls(prev => ({
					...prev,
					monthChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createMonthChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, month: false }));
		}
	};

	const createDayChart = async () => {
		try {
			const isCreateMonth = predictionType !== PredictionType.MONTH

			setChartLoading(prev => ({ ...prev, day: true }));

			const chartYear = selectedYear ? Number(year) : null

			const response = await fetchDayChart({ userId, year: chartYear, month, day, timezone });

			const { dayChart, monthChart } = response.data;
			if (dayChart) {
				const dayHeading = dayChart[0]?.signNumber ?? 1;
				const dayUrl = buildChartUrl(dayChart, dayHeading);

				const monthHeading = monthChart[0]?.signNumber ?? 1;
				const monthUrl = buildChartUrl(monthChart, monthHeading);

				setDayChart(response.data)
				setChartUrls(prev => ({
					...prev,
					dayChartUrl: dayUrl,
					...(isCreateMonth && { monthChartUrl: monthUrl }),
				}));


			}
		} catch (error) {
			console.log("Error in createDayChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, day: false }));
		}
	};

	const createTransitChart = async () => {
		try {
			if (!birthChart) return;
			setChartLoading(prev => ({ ...prev, transit: true }));
			const chartYear = selectedYear ? padithi === PadithiEnum.NADI ? transitYear ? transitYear : year : year : null
			const payload = {
				userId: userId,
				year: chartYear,
				month: padithi === PadithiEnum.NADI ? transitMonth : month,
				day: padithi === PadithiEnum.NADI ? transitDay : day,
				timezone: timezone
			}
			const response = await fetchTransitChart(payload);

			const { transitChart } = response.data;
			if (transitChart) {
				const heading = transitChart[0]?.signNumber ?? 1;
				const url = buildChartUrl(transitChart, heading);

				setChartUrls(prev => ({
					...prev,
					transitChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createTransitChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, transit: false }));
		}
	};


	const createChaturthamsaChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, chaturthamsa: true }));

			const response = await fetchDivisionalChart({
				userId,
				type: DivisionalChartType.CHATURTHAMSA,
			});

			const chart = response?.data?.chaturthamsaChart;
			if (chart) {
				const heading = chart[0]?.signNumber ?? 1;
				const url = buildChartUrl(chart, heading);

				setChartUrls(prev => ({
					...prev,
					chaturthamsaChartUrl: url,
				}));
			}

		} catch (error) {
			console.log("Error in createChaturthamsaChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, chaturthamsa: false }));
		}
	};


	const createSaptamsaChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, saptamsa: true }));

			const response = await fetchDivisionalChart({
				userId,
				type: DivisionalChartType.SAPTAMSA,
			});

			const chart = response?.data?.saptamsaChart;
			if (chart) {
				const heading = chart[0]?.signNumber ?? 1;
				const url = buildChartUrl(chart, heading);

				setChartUrls(prev => ({
					...prev,
					saptamsaChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createSaptamsaChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, saptamsa: false }));
		}
	};

	const createNavamsaChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, navamsa: true }));

			const response = await fetchDivisionalChart({
				userId,
				type: DivisionalChartType.NAVAMSA,
			});

			const chart = response?.data?.navamsaChart;
			if (chart) {
				const heading = chart[0]?.signNumber ?? 1;
				const url = buildChartUrl(chart, heading);

				setChartUrls(prev => ({
					...prev,
					navamsaChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createNavamsaChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, navamsa: false }));
		}
	};

	const createDasamsaChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, dasamsa: true }));

			const response = await fetchDivisionalChart({
				userId,
				type: DivisionalChartType.DASAMSA,
			});

			const chart = response?.data?.dasamsaChart;
			if (chart) {
				const heading = chart[0]?.signNumber ?? 1;
				const url = buildChartUrl(chart, heading);

				setChartUrls(prev => ({
					...prev,
					dasamsaChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createDasamsaChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, dasamsa: false }));
		}
	};

	const createDwadamsaChart = async () => {
		try {

			setChartLoading(prev => ({ ...prev, dwadamsa: true }));

			const response = await fetchDivisionalChart({
				userId,
				type: DivisionalChartType.DWADAMSA,
			});

			const chart = response?.data?.dwadamsaChart;

			if (chart) {
				const heading = chart[0]?.signNumber ?? 1;
				const url = buildChartUrl(chart, heading);

				setChartUrls(prev => ({
					...prev,
					dwadamsaChartUrl: url,
				}));
			}
		} catch (error) {
			console.log("Error in createDwadamsaChart:", error);
		} finally {
			setChartLoading(prev => ({ ...prev, dwadamsa: false }));
		}
	};

	const getUserkarkas = async () => {
		try {
			setChartLoading(prev => ({ ...prev, karkas: true }));
			const response = await fetchUserKarkas({ userId });
			const charaKarkasDasha = response?.data?.charaKarkasDasha;
			setKarkas(charaKarkasDasha)
		} catch (error) {
			console.log("Error in :", error);
		} finally {
			setChartLoading(prev => ({ ...prev, karkas: false }));
		}
	}

	const fetchChartsForUser = async () => {
		try {
			if (previousUserRef.current && previousUserRef.current !== userId) {
				resetData()
			};
			previousUserRef.current = userId
			isFetchingInitialCharts.current = true;

			if ([PadithiEnum.MASTER, PadithiEnum.LAL_KITAB].includes(padithi)) {
				await Promise.all([
					createBirthChart(),
					createYearChart(),
					createMonthChart(),
					createDayChart(),
					createTransitChart(),
				])
			} else if (padithi === PadithiEnum.NADI) {
				await Promise.all([createBirthChart(), createTransitChart()])
			} else if (padithi === PadithiEnum.PARASHARA) {
				await Promise.all([createBirthChart(), createYearChart(), createMonthChart(), createDayChart(), createTransitChart()])
			} else if (padithi === PadithiEnum.JAIMINI) {
				await Promise.all([createBirthChart(), createYearChart(), createChaturthamsaChart(),
				createSaptamsaChart(), createNavamsaChart(), createDasamsaChart(), createDwadamsaChart(), getUserkarkas()
				])
			}

		} catch (error) {
			console.log("Failed to fetch chart ", error)
		} finally {
			resetChartLoading()
			fetchInitialCharts.current = true
		}
	};

	const handlePadithiChange = () => {

		if (!fetchInitialCharts.current) return;
		try {
			if (!userId || !user) return;

			if (padithi !== PadithiEnum.NADI) {
				setTransitDate(null);
				setTransitDay(null);
				setTransitMonth(null);
				setTransitYear(null);
			} else {
				setTransitDay(day);
				setTransitMonth(month);
				setTransitYear(year);
			}
			isFetchingInitialCharts.current = true
			switch (padithi) {
				// ---------------------------------------------
				case PadithiEnum.MASTER:
				case PadithiEnum.LAL_KITAB:
					if (!chartUrls.birthChartUrl) createBirthChart();
					if (!chartUrls.yearChartUrl) createYearChart();
					if (!chartUrls.monthChartUrl) createMonthChart();
					if (!chartUrls.dayChartUrl) createDayChart();
					if (!chartUrls.transitChartUrl) createTransitChart();
					break;

				// ---------------------------------------------
				case PadithiEnum.NADI:
					if (!chartUrls.birthChartUrl) createBirthChart();
					if (!chartUrls.transitChartUrl) createTransitChart();
					break;

				// ---------------------------------------------
				case PadithiEnum.PARASHARA:
					if (!chartUrls.birthChartUrl) createBirthChart();
					if (!chartUrls.yearChartUrl) createYearChart();
					if (!chartUrls.monthChartUrl) createMonthChart();
					if (!chartUrls.dayChartUrl) createDayChart();
					break;

				// ---------------------------------------------
				case PadithiEnum.JAIMINI:
					if (!chartUrls.birthChartUrl) createBirthChart();
					if (!chartUrls.yearChartUrl) createYearChart();
					if (!chartUrls.chaturthamsaChartUrl) createChaturthamsaChart();
					if (!chartUrls.saptamsaChartUrl) createSaptamsaChart();
					if (!chartUrls.navamsaChartUrl) createNavamsaChart();
					if (!chartUrls.dasamsaChartUrl) createDasamsaChart();
					if (!chartUrls.dwadamsaChartUrl) createDwadamsaChart();
					if (!karkas || !karkas.length) getUserkarkas();

					break;

				// ---------------------------------------------
				default:
					break;
			}
		} catch (error) {
			console.log("Ftehc chart error on paditi chnage", error)
		} finally {
			isFetchingInitialCharts.current = false

		}
	};

	const handleTimeChange = () => {
		if (!fetchInitialCharts.current) return;
		switch (predictionType) {
			case PredictionType.YEAR:
				createYearChart()
				createMonthChart()
				createDayChart()
				break;

			case PredictionType.MONTH:
				createMonthChart()
				createDayChart()
				break;

			case PredictionType.DAY:
				createDayChart()
				break;

			default:
				break;
		}
		if (padithi !== PadithiEnum.NADI) createTransitChart()
	}


	// Calculate days in transit month when month or year changes
	useEffect(() => {
		if (!transitYear || !transitMonth) {
			setDaysInTransitMonth([]);
			return;
		}

		const days = moment(
			`${transitYear}-${transitMonth}`,
			"YYYY-M"
		).daysInMonth();
		const daysArray = Array.from({ length: days }, (_, i) => i + 1);
		setDaysInTransitMonth(daysArray);

		// If current selected day exceeds days in new month, reset to last day
		if (transitDay > days) {
			setTransitDay(days);
		}
	}, [transitYear, transitMonth, transitDay]);

	// Update transitDate when day/month/year changes (for Nadi)
	useEffect(() => {
		if (padithi === PadithiEnum.NADI && transitDay && transitMonth && transitYear) {
			const newDate = moment(
				`${transitYear}-${transitMonth}-${transitDay}`,
				"YYYY-M-D"
			).toDate();
			setTransitDate(newDate);
		}
	}, [transitDay, transitMonth, transitYear]);

	const availableYears = Array.from(
		{ length: 2040 - (currentYear - 1) + 1 },
		(_, i) => currentYear - 1 + i
	);

	useEffect(() => {
		handlePadithiChange()
	}, [padithi]);

	useEffect(() => {
		// if (padithi !== PadithiEnum.NADI || !transitDate) return
		setChartUrls(prev => ({ ...prev, transitChartUrl: '' }))
		createTransitChart()
	}, [transitDate])



	useEffect(() => {
		handleTimeChange()
	}, [year, month, day]);


	useEffect(() => {
		if (!userId || !user) return
		fetchInitialCharts.current = false;
		resetData()
		fetchChartsForUser()
	}, [userId, user])

	useEffect(() => {
		if (!userId || !user || !birthChart) return
		createTransitChart()
	}, [birthChart])


	// useEffect(() => {
	// 	if (!year || !padithi || !userId || isFetchingInitialCharts) return
	// 	setChartUrls(prev => ({ ...prev, yearChartUrl: '' }))
	// 	createYearChart()
	// }, [year])

	// useEffect(() => {
	// 	if (!year || !month || !padithi || !userId || isFetchingInitialCharts || !predictionType || predictionType !== PredictionType.MONTH) return
	// 	setChartUrls(prev => ({ ...prev, monthChartUrl: '' }))
	// 	createMonthChart()
	// }, [year, month])

	// useEffect(() => {
	// 	if (!year || !month || !day || !padithi || !userId || isFetchingInitialCharts) return
	// 	if (predictionType || predictionType === PredictionType.DAY) {
	// 		createDayChart()
	// 	}
	// 	if (padithi !== PadithiEnum.NADI) createTransitChart()
	// }, [year, month, day])

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				justifyContent: [PadithiEnum.NADI].includes(padithi) ? "center" : "space-between",
				alignItems: "flex-start",
				gap: [PadithiEnum.NADI, PadithiEnum.PARASHARA].includes(padithi) ? 1 : 1,
				flexWrap: "wrap",
				overflowX: "auto",
				pb: 1,
				minHeight: minHeight
			}}
		>
			{/* Birt Chart For All*/}
			{padithi === PadithiEnum.JAIMINI && (
				<KarkaDetail
					data={karkas}
					loading={chartLoading.karkas}
					maxWidth={maxWidth}
					minWidth={minWidth}
					minHeight={minHeight}
				/>
			)}

			<ChartWithLoader
				url={chartUrls.birthChartUrl}
				title="Birth Chart"
				loading={chartLoading.birth}
				show={true}
				maxWidth={maxWidth}
				minWidth={minWidth}
				minHeight={minHeight}
			/>

			{/* Year Chart For Lallitab and parashara */}
			{[PadithiEnum.MASTER, PadithiEnum.LAL_KITAB, PadithiEnum.PARASHARA].includes(padithi) && (
				<ChartWithLoader
					url={chartUrls.yearChartUrl}
					title={`Year Chart (${year} - ${Number(year) + 1})`}
					loading={chartLoading.year}
					show={true}
					maxWidth={maxWidth}
					minWidth={minWidth}
					minHeight={minHeight}
				/>
			)}

			{/* Monthaly Chart  For (Parashara And Lalkitab )*/}
			{[PadithiEnum.MASTER, PadithiEnum.LAL_KITAB, PadithiEnum.PARASHARA].includes(padithi) && (
				<ChartWithLoader
					url={chartUrls.monthChartUrl}
					title={
						intervalDisplay
							? `Month Chart (${intervalDisplay})`
							: "Month Chart"
					}
					loading={chartLoading.month}
					show={true}
					maxWidth={maxWidth}
					minWidth={minWidth}
					minHeight={minHeight}
				/>
			)}
			{/* Day Chart  */}
			{[PadithiEnum.MASTER, PadithiEnum.LAL_KITAB, PadithiEnum.PARASHARA].includes(padithi) && (
				<ChartWithLoader
					url={chartUrls.dayChartUrl}
					title={`Day Chart (${day}/${month}/${getPredictionYear || year})`}
					loading={chartLoading.day}
					show={true}
					maxWidth={maxWidth}
					minWidth={minWidth}
					minHeight={minHeight}
				/>
			)}

			{/* Transit Chart with inline calendar below (for Nadi only) */}
			{[PadithiEnum.MASTER, PadithiEnum.LAL_KITAB, PadithiEnum.NADI, PadithiEnum.PARASHARA].includes(padithi) &&
				(
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							minHeight: minHeight,
							minWidth: minWidth,
							maxWidth: maxWidth,
						}}
					>
						<ChartWithLoader
							url={chartUrls.transitChartUrl}
							title={
								padithi === PadithiEnum.NADI && transitDate
									? `Transit Chart (${new Date(transitDate).getDate()}/${new Date(transitDate).getMonth() + 1
									}/${new Date(transitDate).getFullYear()})`
									: `Transit Chart (${day}/${month}/${getPredictionYear || year})`
							}
							loading={chartLoading.transit}
							show={true}
							maxWidth={maxWidth}
							minWidth={minWidth}
							minHeight={minHeight}

						/>

						{/* Date Dropdowns for Nadi */}
						{padithi === PadithiEnum.NADI && (
							<Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
								{/* Day Dropdown */}
								<FormControl size="small" sx={{ minWidth: 65, flex: 1 }}>
									<InputLabel id="transit-day-label">Day</InputLabel>
									<Select
										labelId="transit-day-label"
										id="transit-day"
										label="Day"
										value={transitDay}
										onChange={(e) => setTransitDay(e.target.value)}
									>
										{daysInTransitMonth.map((day) => (
											<MenuItem key={day} value={day}>
												{day}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{/* Month Dropdown */}
								<FormControl size="small" sx={{ minWidth: 70, flex: 1 }}>
									<InputLabel id="transit-month-label">Month</InputLabel>
									<Select
										labelId="transit-month-label"
										id="transit-month"
										label="Month"
										value={transitMonth}
										onChange={(e) => setTransitMonth(e.target.value)}
									>
										{Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
											<MenuItem key={month} value={month}>
												{month}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{/* Year Dropdown */}
								<FormControl size="small" sx={{ minWidth: 95, flex: 1 }}>
									<InputLabel id="transit-year-label">Year</InputLabel>
									<Select
										labelId="transit-year-label"
										id="transit-year"
										label="Year"
										value={transitYear}
										onChange={(e) => setTransitYear(e.target.value)}
									>
										{availableYears.map((year) => (
											<MenuItem key={year} value={year}>
												{year}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
						)}

					</Box>
				)}

			{/* Jaimini Charts */}
			{padithi === PadithiEnum.JAIMINI && (
				<>
					<ChartWithLoader
						url={chartUrls.navamsaChartUrl}
						title="Navamsa Chart (D9)"
						loading={chartLoading.navamsa}
						show={true}
						maxWidth={maxWidth}
						minWidth={minWidth}
						minHeight={minHeight}
					/>
					<ChartWithLoader
						url={chartUrls.dasamsaChartUrl}
						title="Dasamsa Chart (D10)"
						loading={chartLoading.dasamsa}
						show={true}
						maxWidth={maxWidth}
						minWidth={minWidth}
						minHeight={minHeight}
					/>


					<ChartSlider
						maxWidth={maxWidth}
						minWidth={minWidth}
						minHeight={minHeight}
						charts={[
							{
								url: chartUrls.chaturthamsaChartUrl,
								title: "Chaturthamsa Chart (D4)",
								loading: chartLoading.chaturthamsa,
								show: true,
							},
							{
								url: chartUrls.dasamsaChartUrl,
								title: "Saptamsa Chart (D7)",
								loading: chartLoading.saptamsa,
								show: true,
							},
							{
								url: chartUrls.dwadamsaChartUrl,
								title: "Dwadamsa Chart (D12)",
								loading: chartLoading.dwadamsa,
								show: true,
							},
						]}
					/>

				</>
			)}

		</Box>
	)
}